import { apiClient } from '@/lib/api'
import { ApiResponse } from '@/types'
import type { ShopShippingSettings } from '@/lib/shippingSettings'
import type { CustomerPortalSettings } from '@/lib/customerPortalSettings'

export interface ShopContext {
  shop_id: number
  shop_code: string
  name: string
  currency?: string
  date_format?: string
  timezone?: string
  contact_person?: string
  phone?: string
  phone_area_code?: string | null
  email?: string
  address_line1?: string
  address_line2?: string
  address_city?: string
  address_state_province?: string
  address_postal_code?: string
  address_country?: string
  address_phone?: string
  guest_customer_id?: number | null
  handling_presets?: import('@/lib/shippingSettings').HandlingPreset[]
  default_incoterm?: string
  supported_content_locales?: string[]
  service_billing_mode?: 'prepaid' | 'hybrid' | 'postpaid'
  service_credit_grant_policy?: 'on_order' | 'on_confirmed'
  /** When false, staff/customer support chat UI and APIs are unavailable. Default true. */
  support_chat_enabled?: boolean
  /** Tenant-shop rental / lease billing. Default false. Never for the platform shop. */
  rental_enabled?: boolean
  /** When true with rental_enabled, track apartment/locker units and occupancy by lease term. */
  rental_track_units?: boolean
  /** Default rental agreement body for new leases (placeholder merge at create). */
  rental_agreement_template?: string | null
  /** Relative uploads path to landlord signature image for rental agreement PDFs. */
  rental_landlord_signature_path?: string | null
  /** Printed signatory name rendered in script on rental agreement PDFs. */
  rental_landlord_signature_name?: string | null
  /** Which customer portal sections are visible. Missing keys default to enabled. */
  customer_portal_settings?: CustomerPortalSettings
  /** Effective storefront/shop logo URL (website logo or shops.logo_path). */
  logo_url?: string | null
  /** Public storefront slug for `/shop/:slug`. Null when website is not configured. */
  website_slug?: string | null
  /** Storefront brand primary color (hex). */
  primary_color?: string | null
  secondary_color?: string | null
  font_family?: string | null
  /** True for the single platform/operator shop (subscription seller). */
  is_platform_shop?: boolean
  /** True when this shop may enable DemoPay. */
  is_demo_shop?: boolean
}

export interface Shop {
  shop_id: number
  shop_code: string
  name: string
  description?: string
  contact_person?: string
  phone?: string
  phone_area_code?: string | null
  email?: string
  address_line1?: string
  address_line2?: string
  address_city?: string
  address_state_province?: string
  address_postal_code?: string
  address_country?: string
  address_phone?: string
  status: 'active' | 'inactive' | 'suspended'
  short_code?: string | null
  short_code_abbrev?: string | null
  website_slug?: string | null
  timezone?: string
  date_format?: string
  currency?: string
  logo_path?: string
  /** FPS (轉數快) — customer HKQR pay with dynamic amount when currency is HKD */
  fps_enabled?: boolean
  fps_account_type?: 'fps_id' | 'phone' | 'email' | 'bank_account' | null
  fps_account_identifier?: string | null
  whatsapp_provider?: 'disabled' | 'textmebot' | 'twilio' | 'meta'
  textmebot_enabled?: boolean
  textmebot_api_key?: string | null
  textmebot_min_gap_seconds?: number
  twilio_account_sid?: string | null
  twilio_auth_token?: string | null
  twilio_whatsapp_from?: string | null
  meta_whatsapp_access_token?: string | null
  meta_whatsapp_phone_number_id?: string | null
  meta_whatsapp_api_version?: string | null
  guest_customer_id?: number | null
  guest_customer?: {
    customer_id: number
    first_name: string
    last_name?: string | null
    email?: string | null
    customer_code?: string | null
  } | null
  supported_content_locales?: string[]
  service_billing_mode?: 'prepaid' | 'hybrid' | 'postpaid'
  service_credit_grant_policy?: 'on_order' | 'on_confirmed'
  support_chat_enabled?: boolean
  rental_enabled?: boolean
  rental_track_units?: boolean
  smtp_mode?: 'platform' | 'shop'
  smtp_host?: string | null
  smtp_port?: number | null
  smtp_user?: string | null
  smtp_from?: string | null
  smtp_password_configured?: boolean
  customer_portal_settings?: CustomerPortalSettings
  /** True for the single platform/operator shop (subscription seller). */
  is_platform_shop?: boolean
  /** True when this shop may enable DemoPay (simulated payments). Super-admin sets this. */
  is_demo_shop?: boolean
  /**
   * When false, storefront catalog stays visible but Add to Cart / checkout are disabled.
   * Platform super-admin only (from shop_website_config).
   */
  storefront_purchasing_enabled?: boolean
  /**
   * When false, shop staff cannot sign in to the OMS. Public storefront and customer login stay on.
   * Platform super-admin only.
   */
  staff_login_enabled?: boolean
  /** Platform super-admin shop list: ShopQuay SaaS entitlement. */
  saas_status?: 'subscribed' | 'trial' | 'expired'
  trial_ends_at?: string | null
  created_at: string
  updated_at: string
}

export interface ShopStats {
  total_customers: number
  total_products: number
  total_orders: number
  total_revenue: number
  total_staff: number
}

export interface CreateShopData {
  name: string
  description?: string
  contact_person?: string
  phone?: string
  phone_area_code?: string | null
  email?: string
  address_line1?: string
  address_line2?: string
  address_city?: string
  address_state_province?: string
  address_postal_code?: string
  address_country?: string
  address_phone?: string
  short_code?: string | null
  short_code_abbrev?: string | null
  timezone?: string
  date_format?: string
  currency?: string
  fps_enabled?: boolean
  fps_account_type?: 'fps_id' | 'phone' | 'email' | 'bank_account' | null
  fps_account_identifier?: string | null
  whatsapp_provider?: 'disabled' | 'textmebot' | 'twilio' | 'meta'
  textmebot_enabled?: boolean
  textmebot_api_key?: string | null
  textmebot_min_gap_seconds?: number
  twilio_account_sid?: string | null
  twilio_auth_token?: string | null
  twilio_whatsapp_from?: string | null
  meta_whatsapp_access_token?: string | null
  meta_whatsapp_phone_number_id?: string | null
  meta_whatsapp_api_version?: string | null
  supported_content_locales?: string[]
  support_chat_enabled?: boolean
  rental_enabled?: boolean
  rental_track_units?: boolean
  rental_agreement_template?: string | null
  rental_landlord_signature_name?: string | null
}

export interface UpdateShopData {
  name?: string
  description?: string
  contact_person?: string
  phone?: string
  phone_area_code?: string | null
  email?: string
  address_line1?: string
  address_line2?: string
  address_city?: string
  address_state_province?: string
  address_postal_code?: string
  address_country?: string
  address_phone?: string
  status?: 'active' | 'inactive' | 'suspended'
  short_code?: string | null
  short_code_abbrev?: string | null
  timezone?: string
  date_format?: string
  currency?: string
  fps_enabled?: boolean
  fps_account_type?: 'fps_id' | 'phone' | 'email' | 'bank_account' | null
  fps_account_identifier?: string | null
  whatsapp_provider?: 'disabled' | 'textmebot' | 'twilio' | 'meta'
  textmebot_enabled?: boolean
  textmebot_api_key?: string | null
  textmebot_min_gap_seconds?: number
  twilio_account_sid?: string | null
  twilio_auth_token?: string | null
  twilio_whatsapp_from?: string | null
  meta_whatsapp_access_token?: string | null
  meta_whatsapp_phone_number_id?: string | null
  meta_whatsapp_api_version?: string | null
  guest_customer_id?: number | null
  supported_content_locales?: string[]
  service_billing_mode?: 'prepaid' | 'hybrid' | 'postpaid'
  service_credit_grant_policy?: 'on_order' | 'on_confirmed'
  support_chat_enabled?: boolean
  rental_enabled?: boolean
  rental_track_units?: boolean
  rental_agreement_template?: string | null
  rental_landlord_signature_name?: string | null
  smtp_mode?: 'platform' | 'shop'
  smtp_host?: string | null
  smtp_port?: number | null
  smtp_user?: string | null
  smtp_password?: string | null
  smtp_from?: string | null
  customer_portal_settings?: CustomerPortalSettings
  /** Super-admin only. */
  is_demo_shop?: boolean
  /** Super-admin only; freezes online purchasing for the shop website. */
  storefront_purchasing_enabled?: boolean
  staff_login_enabled?: boolean
}

export interface ShopsResponse {
  success: boolean
  data: Shop[]
  pagination: {
    current_page: number
    per_page: number
    total_items: number
    total_pages: number
    has_next_page: boolean
    has_prev_page: boolean
  }
}

export const shopService = {
  // Get all shops with pagination and search
  getShops: async (params?: {
    page?: number
    limit?: number
    search?: string
    /** Shop lifecycle: active | inactive | suspended (super_admin list). */
    status?: 'active' | 'inactive' | 'suspended'
    /** ShopQuay SaaS entitlement (super_admin list). */
    billing?: 'subscribed' | 'trial' | 'expired'
    sort_by?: string
    sort_order?: 'ASC' | 'DESC'
  }): Promise<ShopsResponse> => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.search) queryParams.append('search', params.search)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.billing) queryParams.append('billing', params.billing)
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by)
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order)
    
    const url = `/shops${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return apiClient.get(url)
  },

  // Get shop by ID (manager/admin — full shop record)
  getShopById: async (id: number): Promise<ApiResponse<Shop>> => {
    return apiClient.get(`/shops/${id}`)
  },

  // Currency, timezone, address — any authenticated user with shop access
  getShopContext: async (id: number): Promise<ApiResponse<ShopContext>> => {
    return apiClient.get(`/shops/${id}/context`)
  },

  // Create new shop
  createShop: async (data: CreateShopData): Promise<ApiResponse<Shop>> => {
    return apiClient.post('/shops', data)
  },

  // Update shop
  updateShop: async (id: number, data: UpdateShopData): Promise<ApiResponse<Shop>> => {
    return apiClient.put(`/shops/${id}`, data)
  },

  /** Create (if needed) and link Guest / Walk-in for storefront guest checkout. */
  ensureGuestCustomer: async (
    id: number,
  ): Promise<
    ApiResponse<{
      guest_customer_id: number
      guest_customer: {
        customer_id: number
        shop_id: number
        first_name: string | null
        last_name: string | null
        email: string | null
        customer_code: string | null
      }
    }>
  > => {
    return apiClient.post(`/shops/${id}/ensure-guest-customer`)
  },

  testShopSmtp: async (
    id: number,
  ): Promise<ApiResponse<{ source: 'platform' | 'shop' | null }>> => {
    return apiClient.post(`/shops/${id}/smtp/test`)
  },

  // Delete shop
  deleteShop: async (id: number): Promise<ApiResponse<Shop>> => {
    return apiClient.delete(`/shops/${id}`)
  },

  /** Platform super_admin: mark this shop as the sole platform/operator shop. */
  setPlatformShop: async (
    id: number,
  ): Promise<ApiResponse<{ shop_id: number; name: string; is_platform_shop: boolean }>> => {
    return apiClient.post(`/shops/${id}/set-platform`)
  },

  // Upload shop logo
  uploadLogo: async (id: number, file: File): Promise<ApiResponse<{ logo_path: string }>> => {
    const formData = new FormData()
    formData.append('logo', file)
    return apiClient.post(`/shops/${id}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  uploadRentalLandlordSignature: async (
    id: number,
    file: File,
  ): Promise<ApiResponse<{ rental_landlord_signature_path: string }>> => {
    const formData = new FormData()
    formData.append('signature', file)
    return apiClient.post(`/shops/${id}/rental-landlord-signature`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  deleteRentalLandlordSignature: async (id: number): Promise<ApiResponse<unknown>> => {
    return apiClient.delete(`/shops/${id}/rental-landlord-signature`)
  },

  // Get shop statistics
  getShopStats: async (id: number): Promise<ApiResponse<ShopStats>> => {
    return apiClient.get(`/shops/${id}/stats`)
  },

  getShippingSettings: async (id: number): Promise<ApiResponse<ShopShippingSettings>> => {
    return apiClient.get(`/shops/${id}/shipping-settings`)
  },

  updateShippingSettings: async (
    id: number,
    data: Partial<
      Pick<
        ShopShippingSettings,
        | 'ship_to_mode'
        | 'ship_to_country_codes'
        | 'handling_presets'
        | 'default_incoterm'
        | 'show_incoterms_at_checkout'
      >
    >,
  ): Promise<ApiResponse<ShopShippingSettings>> => {
    return apiClient.put(`/shops/${id}/shipping-settings`, data)
  },

  getTaxSettings: async (id: number): Promise<ApiResponse<import('@/lib/taxSettings').ShopTaxSettings>> => {
    return apiClient.get(`/shops/${id}/tax-settings`)
  },

  getSecurityPolicy: async (
    id: number,
  ): Promise<ApiResponse<import('@/services/platformSecurityService').ShopSecurityPolicy>> => {
    return apiClient.get(`/shops/${id}/security-policy`)
  },

  updateTaxSettings: async (
    id: number,
    data: Partial<import('@/lib/taxSettings').ShopTaxSettings>,
  ): Promise<ApiResponse<import('@/lib/taxSettings').ShopTaxSettings>> => {
    return apiClient.put(`/shops/${id}/tax-settings`, data)
  },

  getApprovalSettings: async (
    id: number,
  ): Promise<
    ApiResponse<{
      po_approval_threshold: number | null
      manual_discount_approval_threshold: number | null
    }>
  > => {
    return apiClient.get(`/shops/${id}/approval-settings`)
  },

  updateApprovalSettings: async (
    id: number,
    data: {
      po_approval_threshold?: number | null
      manual_discount_approval_threshold?: number | null
    },
  ): Promise<
    ApiResponse<{
      po_approval_threshold: number | null
      manual_discount_approval_threshold: number | null
    }>
  > => {
    return apiClient.put(`/shops/${id}/approval-settings`, data)
  },

  getCatalogGroups: async (shopId: number): Promise<ApiResponse<string[]>> => {
    return apiClient.get(`/shops/${shopId}/catalog-groups`)
  },

  getCatalogGroupI18n: async (
    shopId: number
  ): Promise<ApiResponse<Array<{ group_key: string; locale: string; display_label: string }>>> => {
    return apiClient.get(`/shops/${shopId}/catalog-group-i18n`)
  },

  upsertCatalogGroupI18n: async (
    shopId: number,
    translations: Array<{ group_key: string; locale: string; display_label: string }>
  ): Promise<ApiResponse<{ upserted: number; deleted: number }>> => {
    return apiClient.put(`/shops/${shopId}/catalog-group-i18n`, { translations })
  },
}